import i18n from '.'
import { nextTick } from 'vue'
import { useCommonStore } from '@/stores/CommonStore'
import { FEATURE_FLAGS } from '@/constants/featureFlags'

export function isSpanishFlagEnabled(): boolean {
  try {
    const commonStore = useCommonStore()
    return commonStore.isFeatureEnabled(FEATURE_FLAGS.SPANISH_VERSION)
  } catch {
    return false
  }
}

const i18nHelpers = {
  get defaultLocale() {
    return 'en'
  },

  get supportedLocales() {
    return ['en', 'es']
  },

  get currentLocale() {
    return i18n.global.locale.value
  },

  set currentLocale(newLocale) {
    i18n.global.locale.value = newLocale
  },

  async switchLanguage(newLocale: string) {
    // When Spanish is not enabled, only allow English
    if (
      !isSpanishFlagEnabled() &&
      newLocale !== i18nHelpers.defaultLocale &&
      newLocale !== i18nHelpers.getPersistedLocale()
    ) {
      return
    }

    try {
      const localeToUse = await i18nHelpers.loadLocaleMessages(newLocale)
      i18nHelpers.saveLocale(localeToUse)
    } catch (error) {
      console.error(
        `Failed to switch language to "${newLocale}". Falling back to "en".`,
        error
      )

      await i18nHelpers.loadLocaleMessages(i18nHelpers.defaultLocale)
      i18nHelpers.saveLocale(i18nHelpers.defaultLocale)
    }
  },

  saveLocale(locale: string) {
    i18nHelpers.currentLocale = locale

    const htmlElement: HTMLElement | null = document.querySelector('html')

    if (htmlElement) {
      htmlElement.setAttribute('lang', locale)
    }

    localStorage.setItem('user-locale', locale)
  },

  async loadLocaleMessages(locale: string): Promise<string> {
    if (!i18n.global.availableLocales.includes(locale)) {
      try {
        const messages = await import(`@/i18n/locales/${locale}.json`)
        i18n.global.setLocaleMessage(locale, messages.default)
      } catch (error) {
        console.error(
          `Failed to load locale messages for "${locale}". Falling back to "en".`,
          error
        )

        if (locale !== i18nHelpers.defaultLocale) {
          return i18nHelpers.loadLocaleMessages(i18nHelpers.defaultLocale)
        }

        locale = i18nHelpers.defaultLocale
      }
    }

    await nextTick()
    return locale
  },

  isLocaleSupported(locale: string) {
    return i18nHelpers.supportedLocales.includes(locale)
  },

  getUserLocale(): { locale: string; localeNoRegion: string } {
    const userNavigator = window.navigator as Navigator & {
      userLanguage?: string
    }
    const locale: string =
      userNavigator.language || userNavigator.userLanguage || 'en' // Replace 'en-US' with a default locale if necessary

    return { locale: locale, localeNoRegion: locale.split('-')[0] }
  },

  getPersistedLocale() {
    const persistedLocale = localStorage.getItem('user-locale') ?? ''

    if (i18nHelpers.isLocaleSupported(persistedLocale)) {
      return persistedLocale
    } else {
      return null
    }
  },

  getBrowserLocale() {
    const userPreferredLocale = i18nHelpers.getUserLocale()

    if (i18nHelpers.isLocaleSupported(userPreferredLocale.locale)) {
      return userPreferredLocale.locale
    }

    if (i18nHelpers.isLocaleSupported(userPreferredLocale.localeNoRegion)) {
      return userPreferredLocale.localeNoRegion
    }

    return i18nHelpers.defaultLocale
  },

  // Call only once user info (and tag-based flags) have loaded. Resets a
  // persisted non-default locale left over from when the flag was on.
  clearStaleSpanishLocale() {
    const persisted = i18nHelpers.getPersistedLocale()

    if (
      persisted &&
      persisted !== i18nHelpers.defaultLocale &&
      !isSpanishFlagEnabled()
    ) {
      localStorage.setItem('user-locale', i18nHelpers.defaultLocale)
    }
  },

  getGuessDefaultLocale() {
    const userPersistedLocale = i18nHelpers.getPersistedLocale()
    if (userPersistedLocale) {
      return userPersistedLocale
    }

    const preferredLocale = i18nHelpers.getBrowserLocale()

    if (
      preferredLocale !== i18nHelpers.defaultLocale &&
      !isSpanishFlagEnabled()
    ) {
      return i18nHelpers.defaultLocale
    }

    i18nHelpers.saveLocale(preferredLocale)

    return preferredLocale
  }
}

export default i18nHelpers
